"use client";

import type React from "react";
import { useState, useEffect, useRef, useMemo, useCallback } from "react";
import {
  Box,
  Container,
  Heading,
  Grid,
  Card,
  CardBody,
  CardHeader,
  Text,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Select,
  VStack,
  Stat,
  StatLabel,
  StatNumber,
  StatHelpText,
  StatArrow,
  Flex,
  Spinner,
  useToast,
  Tooltip,
  Center,
  Button,
  HStack,
  Badge,
  IconButton,
  Progress,
  SimpleGrid,
  Icon,
  Divider,
  Input,
} from "@chakra-ui/react";
import { Calendar, Settings } from "lucide-react";
import { FiMic, FiMessageSquare, FiMail, FiZap, FiTrendingUp } from "react-icons/fi";
import { FaPlayCircle, FaStopCircle, FaDownload } from "react-icons/fa";
import { FiChevronLeft, FiChevronRight } from "react-icons/fi";
import { supabase } from "../supabaseClient";
import { useAuth } from "../contexts/AuthContext";
import {
  CustomizeFieldsDialog,
  type ColumnVisibility,
} from "./usage/CustomizeFieldsDialog";
import { DateRangeDialog } from "./usage/DateRangeDialog";
import { CallDetailsDialog } from "./usage/CallDetailsDialog";
import { SMSDetailsDialog } from "./usage/SMSDetailsDialog";
import { EmailDetailsDialog } from "./usage/EmailDetailsDialog";
import { WhatsAppDetailsDialog } from "./usage/WhatsAppDetailsDialog";

interface UsageStats {
  totalCalls: number | null;
  totalMinutes: number | null;
  totalSMS: number | null;
  totalEmails: number | null;
  totalWhatsApp: number | null;
  avgCallDuration: string | number | null;
  peakHours: string | null;
  topAgent: string | null;
  callsChange: number | null;
  smsChange: number | null;
  whatsappChange: number | null;
  emailsChange: number | null;
}

interface DailyUsage {
  date: string;
  dateRange?: boolean; // date range vs single date
  calls: number;
  minutes: number;
  sms: number;
  emails: number;
  whatsapp: number;
  cost: number;
}

interface AgentUsage {
  name: string;
  calls: number;
  minutes: number;
  cost: number;
}

interface LoadingStates {
  callStats: boolean;
  smsStats: boolean;
  whatsappStats: boolean;
  emailStats: boolean;
  agentUsage: boolean;
  dailyUsage: boolean;
}

const defaultColumnVisibility: ColumnVisibility = {
  time: true,
  duration: true,
  type: true,
  cost: true,
  call_id: true,
  disconnection_reason: true,
  call_status: true,
  user_sentiment: true,
  from: true,
  to: true,
  direction: true,
  call_successful: true,
  latency: true,
  recording: true,
  logs: true,
};

const isMainApp = process.env.REACT_APP_IS_MAIN_APP === "true";
type MetricType = "calls" | "sms" | "email" | "whatsapp" | null;

const InlineSpinner: React.FC = () => (
  <Spinner size="sm" thickness="3px" speed="0.6s" />
);

const renderValue = (
  value: React.ReactNode,
  isLoading: boolean,
  hasValue: boolean,
) => {
  if (isLoading && !hasValue) return <InlineSpinner />;
  if (!hasValue) return <Text color="gray.400">—</Text>;
  return value;
};

const UsagePage: React.FC = () => {
  const { userProfile, isAdmin, isSuperAdmin, currentClientId, clients } = useAuth();
  const [timeRange, setTimeRange] = useState("7d");

  const [loadingStates, setLoadingStates] = useState<LoadingStates>({
    callStats: true,
    smsStats: true,
    whatsappStats: true,
    emailStats: true,
    agentUsage: true,
    dailyUsage: true,
  });

  // IMPORTANT: Use null defaults so we never show fake "0" before real data arrives.
  const [usageStats, setUsageStats] = useState<UsageStats>({
    totalCalls: null,
    totalMinutes: null,
    totalSMS: null,
    totalEmails: null,
    totalWhatsApp: null,
    avgCallDuration: null,
    peakHours: null,
    topAgent: null,
    callsChange: null,
    smsChange: null,
    whatsappChange: null,
    emailsChange: null,
  });

  const [dailyUsage, setDailyUsage] = useState<DailyUsage[]>([]);
  const [agentUsage, setAgentUsage] = useState<AgentUsage[]>([]);
  const toast = useToast();

  const [callDetails, setCallDetails] = useState<any[]>([]);
  const [isLoadingCallDetails, setIsLoadingCallDetails] = useState(false);

  const [columnVisibility, setColumnVisibility] = useState<ColumnVisibility>(
    defaultColumnVisibility,
  );

  const [callDetailsDateRange, setCallDetailsDateRange] = useState<{
    start?: Date;
    end?: Date;
  } | null>(null);

  const [isCustomizeFieldsDialogOpen, setIsCustomizeFieldsDialogOpen] =
    useState(false);
  const [isDateRangeDialogOpen, setIsDateRangeDialogOpen] = useState(false);

  const [currentlyPlaying, setCurrentlyPlaying] = useState<string | null>(null);
  const [audioElement, setAudioElement] = useState<HTMLAudioElement | null>(
    null,
  );

  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10;

  const [filterAgent, setFilterAgent] = useState<string>('all');
  const [filterDirection, setFilterDirection] = useState<string>('all');
  const [filterDuration, setFilterDuration] = useState<string>('all');
  const [filterOutcome, setFilterOutcome] = useState<string>('all');
  const [filterSentiment, setFilterSentiment] = useState<string>('all');
  const [filterCallId, setFilterCallId] = useState<string>('');

  const [selectedCall, setSelectedCall] = useState<any>(null);
  const [isCallDetailsDialogOpen, setIsCallDetailsDialogOpen] = useState(false);

  const isFetchingRef = useRef(false);
  const abortRef = useRef<AbortController | null>(null);
  const convoAbortRef = useRef<AbortController | null>(null);
  const callDetailsAbortRef = useRef<AbortController | null>(null);

  const [smsConversations, setSmsConversations] = useState<any[]>([]);
  const [emailConversations, setEmailConversations] = useState<any[]>([]);
  const [whatsappConversations, setWhatsappConversations] = useState<any[]>([]);

  const [isLoadingSMS, setIsLoadingSMS] = useState(false);
  const [isLoadingEmail, setIsLoadingEmail] = useState(false);
  const [isLoadingWhatsApp, setIsLoadingWhatsApp] = useState(false);

  const [selectedSMSConversation, setSelectedSMSConversation] =
    useState<any>(null);
  const [selectedEmailConversation, setSelectedEmailConversation] =
    useState<any>(null);
  const [selectedWhatsAppConversation, setSelectedWhatsAppConversation] =
    useState<any>(null);

  const [isSMSDialogOpen, setIsSMSDialogOpen] = useState(false);
  const [isEmailDialogOpen, setIsEmailDialogOpen] = useState(false);
  const [isWhatsAppDialogOpen, setIsWhatsAppDialogOpen] = useState(false);

  const [activeMetric, setActiveMetric] = useState<MetricType>("calls");

  // Billing cost summary (current month from usage_records via /api/billing/usage)
  const [billingUsage, setBillingUsage] = useState<any>(null);
  const [billingLoading, setBillingLoading] = useState(true);

  const [smsCurrentPage, setSmsCurrentPage] = useState(1);
  const [emailCurrentPage, setEmailCurrentPage] = useState(1);
  const [whatsappCurrentPage, setWhatsappCurrentPage] = useState(1);

  const [filterCallPhone, setFilterCallPhone] = useState<string>('');
  const [filterSmsPhone, setFilterSmsPhone] = useState<string>('');
  const [filterEmailPhone, setFilterEmailPhone] = useState<string>('');
  const [filterWhatsappPhone, setFilterWhatsappPhone] = useState<string>('');

  const companyName = useMemo(() => {
    if (isAdmin && currentClientId) {
      const selectedClient = clients.find((c) => c.id === currentClientId);
      return selectedClient?.company_name || null;
    }
    return userProfile?.company_name || null;
  }, [isAdmin, currentClientId, clients, userProfile?.company_name]);

  // Fetch current month billing summary
  const fetchBillingUsage = useCallback(async () => {
    try {
      setBillingLoading(true);
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;
      const serverUrl = process.env.REACT_APP_SERVER_URL || "http://localhost:8000";
      const headers: Record<string, string> = {
        "Content-Type": "application/json",
        "user-id": user.id,
        "x-is-admin": isAdmin ? "1" : "0",
      };
      if (isAdmin && currentClientId) {
        const selectedClient = clients.find((c) => c.id === currentClientId);
        if (selectedClient) headers["x-company-name"] = selectedClient.company_name;
      }
      const res = await fetch(`${serverUrl}/api/billing/usage`, { headers });
      if (res.ok) setBillingUsage(await res.json());
    } catch (e) {
      console.error("Failed to load billing usage:", e);
    } finally {
      setBillingLoading(false);
    }
  }, [isAdmin, currentClientId, clients]);

  useEffect(() => {
    if (!userProfile) return;
    if (isSuperAdmin && !currentClientId) return;
    fetchBillingUsage();
  }, [fetchBillingUsage, currentClientId, userProfile]);

  const getCacheKey = (suffix: string) => {
    return `usage_${suffix}_cache_${companyName}_${timeRange}_${
      isAdmin ? "admin" : "user"
    }_${currentClientId ?? "self"}`;
  };

  const CACHE_TTL_MS = 5 * 60 * 1000; // 5 minutes

  const loadFromCache = (cacheKey: string) => {
    try {
      const cached = localStorage.getItem(cacheKey);
      if (cached) {
        const parsed = JSON.parse(cached);
        if (parsed.timestamp && Date.now() - parsed.timestamp < CACHE_TTL_MS) {
          return parsed;
        }
        // Expired — remove stale entry
        localStorage.removeItem(cacheKey);
      }
    } catch (error) {
      console.error("Error loading from cache:", error);
    }
    return null;
  };

  const saveToCache = (cacheKey: string, data: any) => {
    try {
      localStorage.setItem(
        cacheKey,
        JSON.stringify({
          ...data,
          timestamp: Date.now(),
          companyName: companyName,
        }),
      );
    } catch (error) {
      console.error("Error saving to cache:", error);
    }
  };

  const loadCallDetails = async (startDate: Date, endDate: Date) => {
    // Regular timeRange calls are already fetched in loadUsageData
    if (!callDetailsDateRange) return;

    callDetailsAbortRef.current?.abort();
    callDetailsAbortRef.current = new AbortController();
    const signal = callDetailsAbortRef.current.signal;

    setIsLoadingCallDetails(true);
    try {
      const {
        data: { user },
      } = await supabase.auth.getUser();
      if (!user) throw new Error("User not authenticated");

      const startTimestamp = startDate.getTime();
      const endTimestamp = endDate.getTime();

      const serverUrl =
        process.env.REACT_APP_SERVER_URL || "http://localhost:8000";

      const headers: Record<string, string> = {
        "Content-Type": "application/json",
        "user-id": user.id,
        "x-is-admin": isAdmin ? "1" : "0",
      };

      if (isAdmin && currentClientId) {
        const selectedClient = clients.find((c) => c.id === currentClientId);
        if (selectedClient)
          headers["x-company-name"] = selectedClient.company_name;
      }

      const response = await fetch(`${serverUrl}/api/usage/call-stats`, {
        method: "POST",
        headers,
        body: JSON.stringify({
          startTimestamp,
          endTimestamp,
          prevStartTimestamp: 0,
          prevEndTimestamp: 0,
        }),
        signal,
      });

      if (response.ok) {
        const data = await response.json();
        setCallDetails(Array.isArray(data?.calls) ? data.calls : []);
      } else {
        setCallDetails([]);
      }
    } catch (error: any) {
      if (error?.name !== "AbortError") {
        console.error("Error loading call details:", error);
        setCallDetails([]);
      }
    } finally {
      setIsLoadingCallDetails(false);
    }
  };

  useEffect(() => {
    if (!userProfile) return;
    if (isSuperAdmin && !currentClientId) return;
    if (callDetailsDateRange) {
      loadCallDetails(callDetailsDateRange.start!, callDetailsDateRange.end!);
    }
  }, [callDetailsDateRange, currentClientId, userProfile, isAdmin]);

  const handleDateRangeApply = (startDate: Date, endDate: Date) => {
    if (startDate > endDate) {
      toast({
        title: "Invalid date range",
        description: "Start date must be before or equal to end date.",
        status: "error",
        duration: 5000,
        isClosable: true,
      });
      return;
    }
    const adjustedEndDate = new Date(endDate.getTime());
    adjustedEndDate.setHours(23, 59, 59, 999);
    setCallDetailsDateRange({ start: startDate, end: adjustedEndDate });
  };

  const handleClearCustomDateRange = () => {
    setCallDetailsDateRange(null);
    setIsLoadingCallDetails(false);

    // Restore cached "timeRange" call details immediately (best UX)
    const statsCacheKey = getCacheKey("stats");
    const cachedStats = loadFromCache(statsCacheKey);
    if (cachedStats?.callDetails && cachedStats.companyName === companyName) {
      setCallDetails(cachedStats.callDetails);
      return;
    }

    // If no cache, reload timeRange data
    loadUsageData();
  };

  const handleColumnVisibilityChange = (newVisibility: ColumnVisibility) => {
    setColumnVisibility(newVisibility);
  };

  const handleRowClick = (call: any) => {
    setSelectedCall(call);
    setIsCallDetailsDialogOpen(true);
  };

  const formatDuration = (startTime: number, endTime: number) => {
    const duration = Math.floor((endTime - startTime) / 1000);
    const minutes = Math.floor(duration / 60);
    const seconds = duration % 60;
    return `${minutes}:${seconds.toString().padStart(2, "0")}`;
  };

  const formatCost = (cost: number) => {
    return `$${cost.toFixed(3)}`;
  };

  const getSentimentColorSchema = (sentiment: string) => {
    switch (sentiment) {
      case "Positive":
        return "green";
      case "Negative":
        return "red";
      default:
        return "gray";
    }
  };

  const getSentimentBgColor = (sentiment: string) => {
    switch (sentiment) {
      case "Positive":
        return "green.400";
      case "Negative":
        return "red.400";
      default:
        return "gray.400";
    }
  };

  const getStatusColorSchema = (status: string) => {
    switch (status) {
      case "Successful":
        return "green";
      case "Unsuccessful":
        return "red";
      default:
        return "yellow";
    }
  };

  const getStatusBgColor = (status: string) => {
    switch (status) {
      case "Successful":
        return "green.400";
      case "Unsuccessful":
        return "red.400";
      default:
        return "yellow.400";
    }
  };

  const playAudio = (
    callId: string,
    audioUrl: string | undefined,
    e: React.MouseEvent,
  ) => {
    e.stopPropagation();

    if (!audioUrl) {
      toast({
        title: "Recording not available",
        description: "This call doesn't have a recording available.",
        status: "warning",
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    if (currentlyPlaying === callId) {
      if (audioElement) {
        audioElement.pause();
        audioElement.currentTime = 0;
      }
      setCurrentlyPlaying(null);
    } else {
      if (audioElement) {
        audioElement.pause();
        audioElement.currentTime = 0;
      }

      const audio = new Audio(audioUrl);
      audio.onended = () => setCurrentlyPlaying(null);

      audio.play().catch((error) => {
        console.error("Error playing audio:", error);
        toast({
          title: "Playback error",
          description: "There was an error playing this recording.",
          status: "error",
          duration: 3000,
          isClosable: true,
        });
      });

      setAudioElement(audio);
      setCurrentlyPlaying(callId);
    }
  };

  const downloadLogs = (
    callId: string,
    logUrl: string | undefined,
    e: React.MouseEvent,
  ) => {
    e.stopPropagation();

    if (!logUrl) {
      toast({
        title: "Logs not available",
        description: "This call doesn't have logs available for download.",
        status: "warning",
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    const a = document.createElement("a");
    a.href = logUrl;
    a.download = `call-logs-${callId}.txt`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
  };

  useEffect(() => {
    return () => {
      if (audioElement) {
        audioElement.pause();
        audioElement.currentTime = 0;
      }
    };
  }, [audioElement]);

  useEffect(() => {
    if (!userProfile) return;
    if (isSuperAdmin && !currentClientId) return;

    // Always cancel any in-flight load and start a fresh one. (Previously this
    // bailed when a fetch was already running, so when deps settled in stages on
    // reload the aborted run was never replaced and the sections stayed blank.)
    abortRef.current?.abort();
    abortRef.current = new AbortController();

    // Reset all call filters when company or time range changes to avoid stale filter state
    setFilterCallId('');
    setFilterAgent('all');
    setFilterDirection('all');
    setFilterDuration('all');
    setFilterOutcome('all');
    setFilterSentiment('all');
    setFilterCallPhone('');
    setFilterSmsPhone('');
    setFilterEmailPhone('');
    setFilterWhatsappPhone('');
    setCurrentPage(1);

    loadUsageData();

    return () => {
      abortRef.current?.abort();
      callDetailsAbortRef.current?.abort();
    };
  }, [
    timeRange,
    currentClientId,
    userProfile?.company_name,
    userProfile,
    isAdmin,
    companyName,
  ]);

  const getDateRange = (range: string) => {
    const daysMap: Record<string, number> = {
      "1d": 1,
      "7d": 7,
      "30d": 30,
      "90d": 90,
    };
    const days = daysMap[range] || 7;

    const endDate = new Date();
    endDate.setHours(23, 59, 59, 999);

    const startDate = new Date(endDate);
    startDate.setDate(startDate.getDate() - days + 1); // Include today
    startDate.setHours(0, 0, 0, 0);

    return { startDate, endDate, days };
  };

  const loadUsageData = async () => {
    const signal = abortRef.current?.signal;

    const statsCacheKey = getCacheKey("stats");
    const cachedStats = loadFromCache(statsCacheKey);

    const hasValidCache =
      cachedStats && cachedStats.companyName === companyName;

    if (hasValidCache) {
      // show cached immediately (no spinners for cached values)
      if (cachedStats.usageStats) setUsageStats(cachedStats.usageStats);
      if (cachedStats.dailyUsage) setDailyUsage(cachedStats.dailyUsage);
      if (cachedStats.agentUsage) setAgentUsage(cachedStats.agentUsage);
      if (cachedStats.callDetails) setCallDetails(cachedStats.callDetails);
    } else {
      // no cache: show inline spinners (not "0")
      setUsageStats({
        totalCalls: null,
        totalMinutes: null,
        totalSMS: null,
        totalEmails: null,
        totalWhatsApp: null,
        avgCallDuration: null,
        peakHours: null,
        topAgent: null,
        callsChange: null,
        smsChange: null,
        whatsappChange: null,
        emailsChange: null,
      });
      setDailyUsage([]);
      setAgentUsage([]);
      setCallDetails([]);
      setLoadingStates({
        callStats: true,
        smsStats: true,
        whatsappStats: true,
        emailStats: true,
        agentUsage: true,
        dailyUsage: true,
      });
    }

    isFetchingRef.current = true;

    // Start with nulls so we never accidentally show 0 for missing data.
    const fresh = {
      usageStats: {
        totalCalls: hasValidCache
          ? (cachedStats?.usageStats?.totalCalls ?? null)
          : null,
        totalMinutes: hasValidCache
          ? (cachedStats?.usageStats?.totalMinutes ?? null)
          : null,
        totalSMS: hasValidCache
          ? (cachedStats?.usageStats?.totalSMS ?? null)
          : null,
        totalEmails: hasValidCache
          ? (cachedStats?.usageStats?.totalEmails ?? null)
          : null,
        totalWhatsApp: hasValidCache
          ? (cachedStats?.usageStats?.totalWhatsApp ?? null)
          : null,
        avgCallDuration: hasValidCache
          ? (cachedStats?.usageStats?.avgCallDuration ?? null)
          : null,
        peakHours: hasValidCache
          ? (cachedStats?.usageStats?.peakHours ?? null)
          : null,
        topAgent: hasValidCache
          ? (cachedStats?.usageStats?.topAgent ?? null)
          : null,
        callsChange: hasValidCache
          ? (cachedStats?.usageStats?.callsChange ?? null)
          : null,
        smsChange: hasValidCache
          ? (cachedStats?.usageStats?.smsChange ?? null)
          : null,
        whatsappChange: hasValidCache
          ? (cachedStats?.usageStats?.whatsappChange ?? null)
          : null,
        emailsChange: hasValidCache
          ? (cachedStats?.usageStats?.emailsChange ?? null)
          : null,
      } as UsageStats,
      dailyUsage: [] as DailyUsage[],
      agentUsage: [] as AgentUsage[],
      callDetails: [] as any[],
    };

    try {
      const {
        data: { user },
      } = await supabase.auth.getUser();
      if (!user) throw new Error("User not authenticated");

      const { startDate, endDate, days } = getDateRange(timeRange);

      const startTimestamp = startDate.getTime();
      const endTimestamp = endDate.getTime();

      const prevStartDate = new Date(
        startDate.getTime() - days * 24 * 60 * 60 * 1000,
      );
      const prevEndDate = startDate;

      const prevStartTimestamp = prevStartDate.getTime();
      const prevEndTimestamp = prevEndDate.getTime();

      const serverUrl =
        process.env.REACT_APP_SERVER_URL || "http://localhost:8000";

      const headers: Record<string, string> = {
        "Content-Type": "application/json",
        "user-id": user.id,
        "x-is-admin": isAdmin ? "1" : "0",
      };

      if (isAdmin && currentClientId) {
        const selectedClient = clients.find((c) => c.id === currentClientId);
        if (selectedClient)
          headers["x-company-name"] = selectedClient.company_name;
      }

      const fetchPromises: Promise<any>[] = [
        // Call stats
        fetch(`${serverUrl}/api/usage/call-stats`, {
          method: "POST",
          headers,
          body: JSON.stringify({
            startTimestamp,
            endTimestamp,
            prevStartTimestamp,
            prevEndTimestamp,
          }),
          signal,
        })
          .then((res) => (res.ok ? res.json() : null))
          .then((data) => {
            if (data) {
              fresh.usageStats.totalCalls =
                typeof data.totalCalls === "number" ? data.totalCalls : null;
              fresh.usageStats.totalMinutes =
                typeof data.totalMinutes === "number"
                  ? data.totalMinutes
                  : null;
              fresh.usageStats.avgCallDuration = data.avgCallDuration ?? null;
              fresh.usageStats.peakHours =
                typeof data.peakHours === "string" ? data.peakHours : null;
              fresh.usageStats.callsChange =
                typeof data.callsChange === "number" ? data.callsChange : null;

              fresh.callDetails = Array.isArray(data.calls) ? data.calls : [];
              return fresh.callDetails;
            }
            return [];
          })
          .catch((error) => {
            if (error.name !== "AbortError") {
              console.error("Error fetching call stats:", error);
            }
            return [];
          })
          .finally(() => {
            setLoadingStates((prev) => ({ ...prev, callStats: false }));
          }),

        // SMS
        fetch(`${serverUrl}/api/usage/sms-stats`, {
          method: "POST",
          headers,
          body: JSON.stringify({
            startDate: startDate.toISOString(),
            endDate: endDate.toISOString(),
            prevStartDate: prevStartDate.toISOString(),
            prevEndDate: prevEndDate.toISOString(),
          }),
          signal,
        })
          .then((res) => (res.ok ? res.json() : null))
          .then((data) => {
            if (data) {
              fresh.usageStats.totalSMS =
                typeof data.totalSMS === "number" ? data.totalSMS : null;
              fresh.usageStats.smsChange =
                typeof data.smsChange === "number" ? data.smsChange : null;
            }
          })
          .catch((error) => {
            if (error.name !== "AbortError") {
              console.error("Error fetching SMS stats:", error);
            }
          })
          .finally(() => {
            setLoadingStates((prev) => ({ ...prev, smsStats: false }));
          }),

        // WhatsApp
        fetch(`${serverUrl}/api/usage/whatsapp-stats`, {
          method: "POST",
          headers,
          body: JSON.stringify({
            startDate: startDate.toISOString(),
            endDate: endDate.toISOString(),
            prevStartDate: prevStartDate.toISOString(),
            prevEndDate: prevEndDate.toISOString(),
          }),
          signal,
        })
          .then((res) => (res.ok ? res.json() : null))
          .then((data) => {
            if (data) {
              fresh.usageStats.totalWhatsApp =
                typeof data.totalWhatsApp === "number"
                  ? data.totalWhatsApp
                  : null;
              fresh.usageStats.whatsappChange =
                typeof data.whatsappChange === "number"
                  ? data.whatsappChange
                  : null;
            }
          })
          .catch((error) => {
            if (error.name !== "AbortError") {
              console.error("Error fetching WhatsApp stats:", error);
            }
          })
          .finally(() => {
            setLoadingStates((prev) => ({ ...prev, whatsappStats: false }));
          }),
      ];

      if (isMainApp) {
        fetchPromises.push(
          fetch(`${serverUrl}/api/usage/email-stats`, {
            method: "POST",
            headers,
            body: JSON.stringify({
              startDate: startDate.toISOString(),
              endDate: endDate.toISOString(),
              prevStartDate: prevStartDate.toISOString(),
              prevEndDate: prevEndDate.toISOString(),
            }),
            signal,
          })
            .then((res) => (res.ok ? res.json() : null))
            .then((data) => {
              if (data) {
                fresh.usageStats.totalEmails =
                  typeof data.totalEmails === "number"
                    ? data.totalEmails
                    : null;
                fresh.usageStats.emailsChange =
                  typeof data.emailsChange === "number"
                    ? data.emailsChange
                    : null;
              }
            })
            .catch((error) => {
              if (error.name !== "AbortError") {
                console.error("Error fetching email stats:", error);
              }
            })
            .finally(() => {
              setLoadingStates((prev) => ({ ...prev, emailStats: false }));
            }),
        );
      } else {
        setLoadingStates((prev) => ({ ...prev, emailStats: false }));
      }

      const [calls] = await Promise.all(fetchPromises);

      if (calls && Array.isArray(calls) && calls.length > 0) {
        await Promise.all([
          // Agent usage
          fetch(`${serverUrl}/api/usage/agent-usage`, {
            method: "POST",
            headers,
            body: JSON.stringify({ calls }),
            signal,
          })
            .then((res) => (res.ok ? res.json() : null))
            .then((data) => {
              if (data) {
                fresh.agentUsage = Array.isArray(data.agentUsage)
                  ? data.agentUsage
                  : [];
                fresh.usageStats.topAgent =
                  typeof data.topAgent === "string" ? data.topAgent : null;
              }
            })
            .catch((error) => {
              if (error.name !== "AbortError") {
                console.error("Error fetching agent usage:", error);
              }
            })
            .finally(() => {
              setLoadingStates((prev) => ({ ...prev, agentUsage: false }));
            }),

          // Daily usage
          fetch(`${serverUrl}/api/usage/daily-usage`, {
            method: "POST",
            headers,
            body: JSON.stringify({ calls, days }),
            signal,
          })
            .then((res) => (res.ok ? res.json() : null))
            .then((data) => {
              if (data)
                fresh.dailyUsage = Array.isArray(data.dailyUsage)
                  ? data.dailyUsage
                  : [];
            })
            .catch((error) => {
              if (error.name !== "AbortError") {
                console.error("Error fetching daily usage:", error);
              }
            })
            .finally(() => {
              setLoadingStates((prev) => ({ ...prev, dailyUsage: false }));
            }),
        ]);
      } else {
        setLoadingStates((prev) => ({
          ...prev,
          agentUsage: false,
          dailyUsage: false,
        }));
      }

      // If this run was aborted (deps changed mid-flight / reload race), do NOT
      // overwrite the displayed sections or the cache with the empty/partial
      // results — that is what intermittently left Call Details / Daily Usage /
      // Usage by Agent blank (and persisted, since the empty result got cached).
      if (signal?.aborted) return;

      setUsageStats(fresh.usageStats);
      setDailyUsage(fresh.dailyUsage);
      setAgentUsage(fresh.agentUsage);

      if (!callDetailsDateRange) {
        setCallDetails(fresh.callDetails);
      }

      saveToCache(statsCacheKey, {
        usageStats: fresh.usageStats,
        dailyUsage: fresh.dailyUsage,
        agentUsage: fresh.agentUsage,
        callDetails: fresh.callDetails,
      });
    } catch (error: any) {
      if (error?.name !== "AbortError") {
        console.error("Error fetching usage data:", error);
        toast({
          title: "Error loading usage data",
          description: "Failed to fetch usage statistics",
          status: "error",
          duration: 5000,
          isClosable: true,
        });
      }
      setLoadingStates({
        callStats: false,
        smsStats: false,
        whatsappStats: false,
        emailStats: false,
        agentUsage: false,
        dailyUsage: false,
      });
    } finally {
      isFetchingRef.current = false;
    }
  };

  const filteredCalls = callDetails.filter(call => {
    if (filterAgent !== 'all' && call.agent_id !== filterAgent) return false;
    if (filterDirection !== 'all' && (call.direction || 'outbound') !== filterDirection) return false;
    const durSec = (call.duration_ms || 0) / 1000;
    if (filterDuration === 'lt1' && durSec >= 60) return false;
    if (filterDuration === '1to3' && (durSec < 60 || durSec >= 180)) return false;
    if (filterDuration === '3to5' && (durSec < 180 || durSec >= 300)) return false;
    if (filterDuration === 'gt5' && durSec < 300) return false;
    const successful = call.call_status === 'ended' && call.call_analysis?.call_successful;
    if (filterOutcome === 'successful' && !successful) return false;
    if (filterOutcome === 'failed' && successful) return false;
    const sentiment = (call.call_analysis?.sentiment || 'Neutral').toLowerCase();
    if (filterSentiment !== 'all' && sentiment !== filterSentiment) return false;
    if (filterCallId.trim() !== '' && !(call.call_id || '').toLowerCase().includes(filterCallId.trim().toLowerCase())) return false;
    if (filterCallPhone.trim() !== '') {
      const phone = filterCallPhone.trim().toLowerCase();
      const matchTo = (call.to_number || '').toLowerCase().includes(phone);
      const matchFrom = (call.from_number || '').toLowerCase().includes(phone);
      if (!matchTo && !matchFrom) return false;
    }
    return true;
  });

  const uniqueAgents = Array.from(
    new Map(
      callDetails
        .filter(c => c.agent_id)
        .map(c => [c.agent_id, c.agent_name || c.agent_id])
    ).entries()
  ).map(([id, name]) => ({ id, name }));

  const hasActiveFilters = filterAgent !== 'all' || filterDirection !== 'all' || filterDuration !== 'all' || filterOutcome !== 'all' || filterSentiment !== 'all' || filterCallId.trim() !== '' || filterCallPhone.trim() !== '';

  const totalPages = Math.ceil(filteredCalls.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const paginatedCallDetails = filteredCalls.slice(startIndex, endIndex);

  useEffect(() => {
    setCurrentPage(1);
  }, [callDetails]);

  // Reset page to 1 when any filter changes (prevents empty table when page > totalPages)
  useEffect(() => {
    setCurrentPage(1);
  }, [filterCallId, filterAgent, filterDirection, filterDuration, filterOutcome, filterSentiment]);

  const getPageNumbers = () => {
    const pages: (number | string)[] = [];

    if (totalPages <= 7) {
      for (let i = 1; i <= totalPages; i++) pages.push(i);
    } else {
      pages.push(1);

      if (currentPage > 3) pages.push("...");

      const start = Math.max(2, currentPage - 1);
      const end = Math.min(totalPages - 1, currentPage + 1);

      for (let i = start; i <= end; i++) pages.push(i);

      if (currentPage < totalPages - 2) pages.push("...");

      pages.push(totalPages);
    }

    return pages;
  };

  const fetchSMSConversations = async (signal?: AbortSignal) => {
    setIsLoadingSMS(true);
    try {
      const {
        data: { user },
      } = await supabase.auth.getUser();
      if (!user) throw new Error("User not authenticated");

      const { startDate, endDate } = getDateRange(timeRange);
      const serverUrl =
        process.env.REACT_APP_SERVER_URL || "http://localhost:8000";

      const headers: Record<string, string> = {
        "Content-Type": "application/json",
        "user-id": user.id,
        "x-is-admin": isAdmin ? "1" : "0",
      };

      if (isAdmin && currentClientId) {
        const selectedClient = clients.find((c) => c.id === currentClientId);
        if (selectedClient)
          headers["x-company-name"] = selectedClient.company_name;
      }

      const response = await fetch(`${serverUrl}/api/usage/sms-conversations`, {
        method: "POST",
        headers,
        body: JSON.stringify({
          startDate: startDate.toISOString(),
          endDate: endDate.toISOString(),
        }),
        signal,
      });

      if (response.ok) {
        const data = await response.json();
        setSmsConversations(
          Array.isArray(data?.conversations) ? data.conversations : [],
        );
      } else {
        setSmsConversations([]);
      }
    } catch (error: any) {
      if (error?.name !== "AbortError") {
        console.error("Error fetching SMS conversations:", error);
        toast({
          title: "Error loading SMS conversations",
          description: "Failed to fetch SMS conversations",
          status: "error",
          duration: 5000,
          isClosable: true,
        });
        setSmsConversations([]);
      }
      // AbortError: don't touch state — user may have loaded data already
    } finally {
      setIsLoadingSMS(false);
    }
  };

  const fetchEmailConversations = async (signal?: AbortSignal) => {
    setIsLoadingEmail(true);
    try {
      const {
        data: { user },
      } = await supabase.auth.getUser();
      if (!user) throw new Error("User not authenticated");

      const { startDate, endDate } = getDateRange(timeRange);
      const serverUrl =
        process.env.REACT_APP_SERVER_URL || "http://localhost:8000";

      const headers: Record<string, string> = {
        "Content-Type": "application/json",
        "user-id": user.id,
        "x-is-admin": isAdmin ? "1" : "0",
      };

      if (isAdmin && currentClientId) {
        const selectedClient = clients.find((c) => c.id === currentClientId);
        if (selectedClient)
          headers["x-company-name"] = selectedClient.company_name;
      }

      const response = await fetch(
        `${serverUrl}/api/usage/email-conversations`,
        {
          method: "POST",
          headers,
          body: JSON.stringify({
            startDate: startDate.toISOString(),
            endDate: endDate.toISOString(),
          }),
          signal,
        },
      );

      if (response.ok) {
        const data = await response.json();
        setEmailConversations(
          Array.isArray(data?.conversations) ? data.conversations : [],
        );
      } else {
        setEmailConversations([]);
      }
    } catch (error: any) {
      if (error?.name !== "AbortError") {
        console.error("Error fetching email conversations:", error);
        toast({
          title: "Error loading email conversations",
          description: "Failed to fetch email conversations",
          status: "error",
          duration: 5000,
          isClosable: true,
        });
        setEmailConversations([]);
      }
      // AbortError: don't touch state
    } finally {
      setIsLoadingEmail(false);
    }
  };

  const fetchWhatsAppConversations = async (signal?: AbortSignal) => {
    setIsLoadingWhatsApp(true);
    try {
      const {
        data: { user },
      } = await supabase.auth.getUser();
      if (!user) throw new Error("User not authenticated");

      const { startDate, endDate } = getDateRange(timeRange);
      const serverUrl =
        process.env.REACT_APP_SERVER_URL || "http://localhost:8000";

      const headers: Record<string, string> = {
        "Content-Type": "application/json",
        "user-id": user.id,
        "x-is-admin": isAdmin ? "1" : "0",
      };

      if (isAdmin && currentClientId) {
        const selectedClient = clients.find((c) => c.id === currentClientId);
        if (selectedClient)
          headers["x-company-name"] = selectedClient.company_name;
      }

      const response = await fetch(
        `${serverUrl}/api/usage/whatsapp-conversations`,
        {
          method: "POST",
          headers,
          body: JSON.stringify({
            startDate: startDate.toISOString(),
            endDate: endDate.toISOString(),
          }),
          signal,
        },
      );

      if (response.ok) {
        const data = await response.json();
        setWhatsappConversations(
          Array.isArray(data?.conversations) ? data.conversations : [],
        );
      } else {
        setWhatsappConversations([]);
      }
    } catch (error: any) {
      if (error?.name !== "AbortError") {
        console.error("Error fetching WhatsApp conversations:", error);
        toast({
          title: "Error loading WhatsApp conversations",
          description: "Failed to fetch WhatsApp conversations",
          status: "error",
          duration: 5000,
          isClosable: true,
        });
        setWhatsappConversations([]);
      }
      // AbortError: don't touch state
    } finally {
      setIsLoadingWhatsApp(false);
    }
  };

  useEffect(() => {
    if (!userProfile) return;
    if (isSuperAdmin && !currentClientId) return;

    convoAbortRef.current?.abort();
    convoAbortRef.current = new AbortController();
    const signal = convoAbortRef.current.signal;

    if (activeMetric === "sms") {
      fetchSMSConversations(signal);
    } else if (activeMetric === "email") {
      fetchEmailConversations(signal);
    } else if (activeMetric === "whatsapp") {
      fetchWhatsAppConversations(signal);
    }

    return () => {
      convoAbortRef.current?.abort();
    };
  }, [activeMetric, timeRange, currentClientId, userProfile, isAdmin, isSuperAdmin]);

  useEffect(() => setSmsCurrentPage(1), [smsConversations]);
  useEffect(() => setEmailCurrentPage(1), [emailConversations]);
  useEffect(() => setWhatsappCurrentPage(1), [whatsappConversations]);

  useEffect(() => setSmsCurrentPage(1), [filterSmsPhone]);
  useEffect(() => setEmailCurrentPage(1), [filterEmailPhone]);
  useEffect(() => setWhatsappCurrentPage(1), [filterWhatsappPhone]);

  const filteredSMS = filterSmsPhone.trim()
    ? smsConversations.filter((c: any) => {
        const phone = filterSmsPhone.trim().toLowerCase();
        return (c.sender_id || '').toLowerCase().includes(phone) ||
               (c.receiver_id || '').toLowerCase().includes(phone);
      })
    : smsConversations;

  const filteredEmail = filterEmailPhone.trim()
    ? emailConversations.filter((c: any) => {
        const phone = filterEmailPhone.trim().toLowerCase();
        return (c.sender_id || '').toLowerCase().includes(phone) ||
               (c.receiver_id || '').toLowerCase().includes(phone);
      })
    : emailConversations;

  const filteredWhatsApp = filterWhatsappPhone.trim()
    ? whatsappConversations.filter((c: any) => {
        const phone = filterWhatsappPhone.trim().toLowerCase();
        return (c.sender_id || '').toLowerCase().includes(phone) ||
               (c.receiver_id || '').toLowerCase().includes(phone);
      })
    : whatsappConversations;

  const smsTotalPages = Math.ceil(filteredSMS.length / itemsPerPage);
  const smsStartIndex = (smsCurrentPage - 1) * itemsPerPage;
  const smsEndIndex = smsStartIndex + itemsPerPage;
  const paginatedSMS = filteredSMS.slice(smsStartIndex, smsEndIndex);

  const emailTotalPages = Math.ceil(filteredEmail.length / itemsPerPage);
  const emailStartIndex = (emailCurrentPage - 1) * itemsPerPage;
  const emailEndIndex = emailStartIndex + itemsPerPage;
  const paginatedEmail = filteredEmail.slice(
    emailStartIndex,
    emailEndIndex,
  );

  const whatsappTotalPages = Math.ceil(
    filteredWhatsApp.length / itemsPerPage,
  );
  const whatsappStartIndex = (whatsappCurrentPage - 1) * itemsPerPage;
  const whatsappEndIndex = whatsappStartIndex + itemsPerPage;
  const paginatedWhatsApp = filteredWhatsApp.slice(
    whatsappStartIndex,
    whatsappEndIndex,
  );

  const getPageNumbersForTable = (total: number, current: number) => {
    const pages: (number | string)[] = [];

    if (total <= 7) {
      for (let i = 1; i <= total; i++) pages.push(i);
    } else {
      pages.push(1);

      if (current > 3) pages.push("...");

      const start = Math.max(2, current - 1);
      const end = Math.min(total - 1, current + 1);

      for (let i = start; i <= end; i++) pages.push(i);

      if (current < total - 2) pages.push("...");

      pages.push(total);
    }

    return pages;
  };

  const isAnyLoading = Object.values(loadingStates).some(Boolean);
  // Super admins: need currentClientId (they pick a company from dropdown).
  // Account admins: scoped to their own company — currentClientId is auto-assigned but
  //   the backend already uses their profile company, so we don't need to wait for it.
  // Regular users: just need userProfile.
  const isAuthReady = userProfile && (!isSuperAdmin || currentClientId);

  // Helpers for rendering stat numbers without ever showing fake zeros
  const hasCalls = typeof usageStats.totalCalls === "number";
  const hasSMS = typeof usageStats.totalSMS === "number";
  const hasWhatsApp = typeof usageStats.totalWhatsApp === "number";
  const hasEmails = typeof usageStats.totalEmails === "number";
  const hasMinutes = typeof usageStats.totalMinutes === "number";
  const hasCallsChange = typeof usageStats.callsChange === "number";
  const hasSmsChange = typeof usageStats.smsChange === "number";
  const hasWaChange = typeof usageStats.whatsappChange === "number";
  const hasEmailChange = typeof usageStats.emailsChange === "number";
  const hasPeak =
    typeof usageStats.peakHours === "string" && usageStats.peakHours.length > 0;
  const hasTopAgent =
    typeof usageStats.topAgent === "string" && usageStats.topAgent.length > 0;
  const hasAvgDur =
    usageStats.avgCallDuration !== null &&
    usageStats.avgCallDuration !== undefined &&
    String(usageStats.avgCallDuration).length > 0;

  return (
    <Container maxW="container.2xl" py={8}>
      <VStack spacing={8} align="stretch">
        <Flex justify="space-between" align="center">
          <Heading size="lg" color="gray.800">
            Usage History
          </Heading>

          <Select
            value={timeRange}
            onChange={(e) => setTimeRange(e.target.value)}
            maxW="200px"
          >
            <option value="1d">Last 24 Hours</option>
            <option value="7d">Last 7 Days</option>
            <option value="30d">Last 30 Days</option>
            <option value="90d">Last 90 Days</option>
          </Select>
        </Flex>

        {!isAuthReady ? (
          <Center py={20}>
            <VStack spacing={4}>
              <Spinner size="xl" color="blue.500" thickness="4px" />
              <Text color="gray.600">Loading company data...</Text>
            </VStack>
          </Center>
        ) : (
          <>
            {/* ── Current Month Billing Costs ──────────────────────────── */}
            <Card mb={2}>
              <CardHeader pb={2}>
                <Flex justify="space-between" align="center">
                  <HStack>
                    <Icon as={FiTrendingUp} />
                    <Heading size="md">Current Month Billing Costs</Heading>
                  </HStack>
                  {billingUsage && (
                    <Badge colorScheme="purple" fontSize="sm" px={3} py={1}>
                      {billingUsage.period}
                    </Badge>
                  )}
                </Flex>
              </CardHeader>
              <CardBody>
                {billingLoading ? (
                  <Center py={4}><Spinner size="md" /></Center>
                ) : billingUsage ? (
                  <>
                    <SimpleGrid columns={{ base: 2, md: 4 }} spacing={4} mb={4}>
                      <Stat>
                        <StatLabel><HStack spacing={1}><Icon as={FiMic} /><Text>Voice</Text></HStack></StatLabel>
                        <StatNumber fontSize="lg">${billingUsage.voice?.cost?.toFixed(2) ?? "0.00"}</StatNumber>
                        <StatHelpText>{(billingUsage.voice?.quantity ?? 0).toFixed(1)} min · $0.30/min</StatHelpText>
                      </Stat>
                      <Stat>
                        <StatLabel><HStack spacing={1}><Icon as={FiMessageSquare} /><Text>SMS</Text></HStack></StatLabel>
                        <StatNumber fontSize="lg">${billingUsage.sms?.cost?.toFixed(2) ?? "0.00"}</StatNumber>
                        <StatHelpText>{billingUsage.sms?.quantity ?? 0} msgs · $0.02/msg</StatHelpText>
                      </Stat>
                      <Stat>
                        <StatLabel><HStack spacing={1}><Icon as={FiMail} /><Text>Email</Text></HStack></StatLabel>
                        <StatNumber fontSize="lg">${billingUsage.email?.cost?.toFixed(2) ?? "0.00"}</StatNumber>
                        <StatHelpText>{billingUsage.email?.quantity ?? 0} emails · $0.02/email</StatHelpText>
                      </Stat>
                      <Stat>
                        <StatLabel><HStack spacing={1}><Icon as={FiZap} /><Text>WhatsApp</Text></HStack></StatLabel>
                        <StatNumber fontSize="lg">${billingUsage.whatsapp?.cost?.toFixed(2) ?? "0.00"}</StatNumber>
                        <StatHelpText>{billingUsage.whatsapp?.quantity ?? 0} msgs · $0.02/msg</StatHelpText>
                      </Stat>
                    </SimpleGrid>
                    <Divider mb={3} />
                    <Box>
                      <Flex justify="space-between" mb={1}>
                        <Text fontWeight="medium">
                          Total this month: <b>${(billingUsage.totalCost ?? 0).toFixed(2)}</b>
                        </Text>
                        <Text fontSize="sm" color="gray.500">
                          Auto-billed at $100 · ${(billingUsage.remainingToThreshold ?? 100).toFixed(2)} remaining
                        </Text>
                      </Flex>
                      <Progress
                        value={((billingUsage.totalCost ?? 0) / (billingUsage.threshold ?? 100)) * 100}
                        colorScheme={(billingUsage.totalCost ?? 0) >= 80 ? "orange" : "purple"}
                        borderRadius="full"
                        size="sm"
                      />
                      <Text fontSize="xs" color="gray.400" mt={1}>
                        These costs are billed via Stripe. You are automatically invoiced at every $100 of usage.
                      </Text>
                    </Box>
                  </>
                ) : (
                  <Text color="gray.500" textAlign="center" py={2}>No billing data available yet.</Text>
                )}
              </CardBody>
            </Card>

            {/* Metrics Grid */}
            <Grid
              templateColumns="repeat(auto-fit, minmax(240px, 1fr))"
              gap={6}
              mb={8}
            >
              {/* Total Calls */}
              <Tooltip label="Click to view call details" hasArrow>
                <Card
                  cursor="pointer"
                  onClick={() =>
                    setActiveMetric(activeMetric === "calls" ? null : "calls")
                  }
                  bg={activeMetric === "calls" ? "primary.50" : undefined}
                  borderColor={
                    activeMetric === "calls" ? "primary.500" : undefined
                  }
                  borderWidth={activeMetric === "calls" ? "2px" : "1px"}
                  _hover={{
                    shadow: "xl",
                    transform: "translateY(-4px) scale(1.02)",
                    transition: "all 0.3s ease",
                  }}
                  transition="all 0.3s ease"
                >
                  <CardBody>
                    <Stat>
                      <StatLabel>Total Calls</StatLabel>
                      <StatNumber color="primary.500">
                        {renderValue(
                          hasCalls
                            ? usageStats.totalCalls!.toLocaleString()
                            : null,
                          loadingStates.callStats,
                          hasCalls,
                        )}
                      </StatNumber>
                      <StatHelpText>
                        {renderValue(
                          hasCallsChange ? (
                            <>
                              <StatArrow
                                type={
                                  usageStats.callsChange! >= 0
                                    ? "increase"
                                    : "decrease"
                                }
                              />
                              {Math.abs(usageStats.callsChange!).toFixed(1)}%
                              from last period
                            </>
                          ) : null,
                          loadingStates.callStats,
                          hasCallsChange,
                        )}
                      </StatHelpText>
                    </Stat>
                  </CardBody>
                </Card>
              </Tooltip>

              {/* SMS */}
              <Tooltip label="Click to view SMS details" hasArrow>
                <Card
                  cursor="pointer"
                  onClick={() =>
                    setActiveMetric(activeMetric === "sms" ? null : "sms")
                  }
                  bg={activeMetric === "sms" ? "blue.50" : undefined}
                  borderColor={activeMetric === "sms" ? "blue.500" : undefined}
                  borderWidth={activeMetric === "sms" ? "2px" : "1px"}
                  _hover={{
                    shadow: "xl",
                    transform: "translateY(-4px) scale(1.02)",
                    transition: "all 0.3s ease",
                  }}
                  transition="all 0.3s ease"
                >
                  <CardBody>
                    <Stat>
                      <StatLabel>SMS Messages</StatLabel>
                      <StatNumber color="blue.500">
                        {renderValue(
                          hasSMS ? usageStats.totalSMS!.toLocaleString() : null,
                          loadingStates.smsStats,
                          hasSMS,
                        )}
                      </StatNumber>
                      <StatHelpText>
                        {renderValue(
                          hasSmsChange ? (
                            <>
                              <StatArrow
                                type={
                                  usageStats.smsChange! >= 0
                                    ? "increase"
                                    : "decrease"
                                }
                              />
                              {Math.abs(usageStats.smsChange!).toFixed(1)}% from
                              last period
                            </>
                          ) : null,
                          loadingStates.smsStats,
                          hasSmsChange,
                        )}
                      </StatHelpText>
                    </Stat>
                  </CardBody>
                </Card>
              </Tooltip>

              {/* WhatsApp */}
              <Tooltip label="Click to view WhatsApp details" hasArrow>
                <Card
                  cursor="pointer"
                  onClick={() =>
                    setActiveMetric(
                      activeMetric === "whatsapp" ? null : "whatsapp",
                    )
                  }
                  bg={activeMetric === "whatsapp" ? "green.50" : undefined}
                  borderColor={
                    activeMetric === "whatsapp" ? "green.500" : undefined
                  }
                  borderWidth={activeMetric === "whatsapp" ? "2px" : "1px"}
                  _hover={{
                    shadow: "xl",
                    transform: "translateY(-4px) scale(1.02)",
                    transition: "all 0.3s ease",
                  }}
                  transition="all 0.3s ease"
                >
                  <CardBody>
                    <Stat>
                      <StatLabel>WhatsApp Messages</StatLabel>
                      <StatNumber color="green.600">
                        {renderValue(
                          hasWhatsApp
                            ? usageStats.totalWhatsApp!.toLocaleString()
                            : null,
                          loadingStates.whatsappStats,
                          hasWhatsApp,
                        )}
                      </StatNumber>
                      <StatHelpText>
                        {renderValue(
                          hasWaChange ? (
                            <>
                              <StatArrow
                                type={
                                  usageStats.whatsappChange! >= 0
                                    ? "increase"
                                    : "decrease"
                                }
                              />
                              {Math.abs(usageStats.whatsappChange!).toFixed(1)}%
                              from last period
                            </>
                          ) : null,
                          loadingStates.whatsappStats,
                          hasWaChange,
                        )}
                      </StatHelpText>
                    </Stat>
                  </CardBody>
                </Card>
              </Tooltip>

              {/* Email */}
              {isMainApp && (
                <Tooltip label="Click to view email details" hasArrow>
                  <Card
                    cursor="pointer"
                    onClick={() =>
                      setActiveMetric(activeMetric === "email" ? null : "email")
                    }
                    bg={activeMetric === "email" ? "orange.50" : undefined}
                    borderColor={
                      activeMetric === "email" ? "orange.500" : undefined
                    }
                    borderWidth={activeMetric === "email" ? "2px" : "1px"}
                    _hover={{
                      shadow: "xl",
                      transform: "translateY(-4px) scale(1.02)",
                      transition: "all 0.3s ease",
                    }}
                    transition="all 0.3s ease"
                  >
                    <CardBody>
                      <Stat>
                        <StatLabel>Email Messages</StatLabel>
                        <StatNumber color="orange.500">
                          {renderValue(
                            hasEmails
                              ? usageStats.totalEmails!.toLocaleString()
                              : null,
                            loadingStates.emailStats,
                            hasEmails,
                          )}
                        </StatNumber>
                        <StatHelpText>
                          {renderValue(
                            hasEmailChange ? (
                              <>
                                <StatArrow
                                  type={
                                    usageStats.emailsChange! >= 0
                                      ? "increase"
                                      : "decrease"
                                  }
                                />
                                {Math.abs(usageStats.emailsChange!).toFixed(1)}%
                                from last period
                              </>
                            ) : null,
                            loadingStates.emailStats,
                            hasEmailChange,
                          )}
                        </StatHelpText>
                      </Stat>
                    </CardBody>
                  </Card>
                </Tooltip>
              )}
            </Grid>

            {activeMetric === "calls" && (
              <Card mb={8}>
                <CardHeader
                  bg="gradient-to-r from-blue-50 to-purple-50"
                  borderBottom="1px solid"
                  borderColor="gray.200"
                >
                  <Flex justify="space-between" align="center">
                    <Heading size="md">Call Details</Heading>
                    <HStack spacing={4}>
                      <Button
                        leftIcon={<Calendar />}
                        variant="outline"
                        size="sm"
                        onClick={() => setIsDateRangeDialogOpen(true)}
                        colorScheme="gray"
                      >
                        {callDetailsDateRange?.start &&
                        callDetailsDateRange?.end
                          ? `${callDetailsDateRange.start.toLocaleDateString()} - ${callDetailsDateRange.end.toLocaleDateString()}`
                          : `Date Range (${
                              timeRange === "1d"
                                ? "Last 24 Hours"
                                : timeRange === "7d"
                                  ? "Last 7 Days"
                                  : timeRange === "30d"
                                    ? "Last 30 Days"
                                    : "Last 90 Days"
                            })`}
                      </Button>

                      {callDetailsDateRange && (
                        <Button
                          size="sm"
                          variant="ghost"
                          colorScheme="red"
                          onClick={handleClearCustomDateRange}
                        >
                          Clear Custom Range
                        </Button>
                      )}

                      <Button
                        leftIcon={<Settings />}
                        variant="outline"
                        size="sm"
                        onClick={() => setIsCustomizeFieldsDialogOpen(true)}
                        colorScheme="gray"
                      >
                        Customize
                      </Button>
                    </HStack>
                  </Flex>
                </CardHeader>

                <CardBody>
                  {isLoadingCallDetails ||
                  (loadingStates.callStats && callDetails.length === 0) ? (
                    <Center py={8}>
                      <Spinner size="lg" color="blue.500" />
                    </Center>
                  ) : callDetails.length === 0 ? (
                    <Text color="gray.600" textAlign="center" py={4}>
                      No call details available
                    </Text>
                  ) : (
                    <>
                      {/* ── Filter Bar ─────────────────────────────────────────────────── */}
                      <Flex wrap="wrap" gap={3} mb={4} align="center">
                        {/* Agent */}
                        <Select
                          size="sm"
                          maxW="180px"
                          value={filterAgent}
                          onChange={(e) => { setFilterAgent(e.target.value); setCurrentPage(1); }}
                        >
                          <option value="all">All Agents</option>
                          {uniqueAgents.map(a => (
                            <option key={a.id} value={a.id}>{a.name}</option>
                          ))}
                        </Select>

                        {/* Direction */}
                        <Select
                          size="sm"
                          maxW="140px"
                          value={filterDirection}
                          onChange={(e) => { setFilterDirection(e.target.value); setCurrentPage(1); }}
                        >
                          <option value="all">All Directions</option>
                          <option value="inbound">Inbound</option>
                          <option value="outbound">Outbound</option>
                        </Select>

                        {/* Duration */}
                        <Select
                          size="sm"
                          maxW="150px"
                          value={filterDuration}
                          onChange={(e) => { setFilterDuration(e.target.value); setCurrentPage(1); }}
                        >
                          <option value="all">Any Duration</option>
                          <option value="lt1">Under 1 min</option>
                          <option value="1to3">1 – 3 min</option>
                          <option value="3to5">3 – 5 min</option>
                          <option value="gt5">Over 5 min</option>
                        </Select>

                        {/* Outcome */}
                        <Select
                          size="sm"
                          maxW="150px"
                          value={filterOutcome}
                          onChange={(e) => { setFilterOutcome(e.target.value); setCurrentPage(1); }}
                        >
                          <option value="all">All Outcomes</option>
                          <option value="successful">Successful</option>
                          <option value="failed">Failed</option>
                        </Select>

                        {/* Sentiment */}
                        <Select
                          size="sm"
                          maxW="150px"
                          value={filterSentiment}
                          onChange={(e) => { setFilterSentiment(e.target.value); setCurrentPage(1); }}
                        >
                          <option value="all">All Sentiments</option>
                          <option value="positive">Positive</option>
                          <option value="neutral">Neutral</option>
                          <option value="negative">Negative</option>
                        </Select>

                        {/* Call ID search */}
                        <Input
                          size="sm"
                          maxW="220px"
                          placeholder="Search by Call ID…"
                          value={filterCallId}
                          onChange={(e) => { setFilterCallId(e.target.value); setCurrentPage(1); }}
                        />

                        {/* Phone search */}
                        <Input
                          size="sm"
                          maxW="180px"
                          placeholder="Search by phone…"
                          value={filterCallPhone}
                          onChange={(e) => { setFilterCallPhone(e.target.value); setCurrentPage(1); }}
                        />

                        {/* Clear filters */}
                        {hasActiveFilters && (
                          <Button
                            size="sm"
                            variant="ghost"
                            colorScheme="red"
                            onClick={() => {
                            setFilterAgent('all');
                            setFilterDirection('all');
                            setFilterDuration('all');
                            setFilterOutcome('all');
                            setFilterSentiment('all');
                            setFilterCallId('');
                            setFilterCallPhone('');
                            setCurrentPage(1);
                            }}
                          >
                            Clear Filters
                          </Button>
                        )}

                        {/* Count badge */}
                        <Text fontSize="sm" color="gray.500" ml="auto">
                          Showing {filteredCalls.length} of {callDetails.length} calls
                        </Text>
                      </Flex>

                      <Box overflowX="auto">
                        <Table variant="simple" size="sm">
                          <Thead>
                            <Tr>
                              {columnVisibility.time && <Th>Time</Th>}
                              {columnVisibility.duration && <Th>Duration</Th>}
                              {columnVisibility.type && <Th>Type</Th>}
                              {columnVisibility.cost && <Th>Cost</Th>}
                              {columnVisibility.call_id && <Th>Call ID</Th>}
                              {columnVisibility.disconnection_reason && (
                                <Th>End Reason</Th>
                              )}
                              {columnVisibility.call_status && <Th>Status</Th>}
                              {columnVisibility.user_sentiment && (
                                <Th>Sentiment</Th>
                              )}
                              {columnVisibility.from && <Th>From</Th>}
                              {columnVisibility.to && <Th>To</Th>}
                              {columnVisibility.direction && <Th>Direction</Th>}
                              {columnVisibility.call_successful && (
                                <Th>Outcome</Th>
                              )}
                              {columnVisibility.latency && <Th>Latency</Th>}
                              {columnVisibility.recording && <Th>Recording</Th>}
                              {columnVisibility.logs && <Th>Logs</Th>}
                            </Tr>
                          </Thead>

                          <Tbody>
                            {paginatedCallDetails.map((call: any) => (
                              <Tr
                                key={call.call_id}
                                onClick={() => handleRowClick(call)}
                                _hover={{
                                  bg: "gray.50",
                                  transform: "translateX(2px)",
                                  transition: "all 0.2s ease",
                                }}
                                transition="all 0.2s ease"
                                cursor="pointer"
                              >
                                {columnVisibility.time && (
                                  <Td>
                                    {new Date(
                                      call.start_timestamp,
                                    ).toLocaleString()}
                                  </Td>
                                )}
                                {columnVisibility.duration && (
                                  <Td>
                                    {formatDuration(
                                      call.start_timestamp,
                                      call.end_timestamp,
                                    )}
                                  </Td>
                                )}
                                {columnVisibility.type && (
                                  <Td>{call.call_type}</Td>
                                )}
                                {columnVisibility.cost && (
                                  <Td>
                                    {formatCost(
                                      call.cost ?? (call.call_cost?.combined_cost ?? 0),
                                    )}
                                  </Td>
                                )}
                                {columnVisibility.call_id && (
                                  <Td fontSize="xs">{call.call_id}</Td>
                                )}
                                {columnVisibility.disconnection_reason && (
                                  <Td>{call.disconnection_reason || "N/A"}</Td>
                                )}
                                {columnVisibility.call_status && (
                                  <Td>{call.call_status}</Td>
                                )}
                                {columnVisibility.user_sentiment && (
                                  <Td>
                                    <Badge
                                      colorScheme={getSentimentColorSchema(
                                        call.call_analysis?.sentiment ||
                                          "Neutral",
                                      )}
                                      bg={getSentimentBgColor(
                                        call.call_analysis?.sentiment ||
                                          "Neutral",
                                      )}
                                      color="white"
                                    >
                                      {call.call_analysis?.sentiment ||
                                        "Neutral"}
                                    </Badge>
                                  </Td>
                                )}
                                {columnVisibility.from && (
                                  <Td>{call.from_number}</Td>
                                )}
                                {columnVisibility.to && (
                                  <Td>{call.to_number}</Td>
                                )}
                                {columnVisibility.direction && (
                                  <Td>
                                    <Badge
                                      colorScheme={
                                        call.direction === "inbound"
                                          ? "green"
                                          : "purple"
                                      }
                                      variant="subtle"
                                    >
                                      {call.direction || "outbound"}
                                    </Badge>
                                  </Td>
                                )}
                                {columnVisibility.call_successful && (
                                  <Td>
                                    <Badge
                                      colorScheme={getStatusColorSchema(
                                        call.call_status === "ended" &&
                                          call.call_analysis?.call_successful
                                          ? "Successful"
                                          : "Unsuccessful",
                                      )}
                                      bg={getStatusBgColor(
                                        call.call_status === "ended" &&
                                          call.call_analysis?.call_successful
                                          ? "Successful"
                                          : "Unsuccessful",
                                      )}
                                      color="white"
                                    >
                                      {call.call_status === "ended" &&
                                      call.call_analysis?.call_successful
                                        ? "Success"
                                        : "Failed"}
                                    </Badge>
                                  </Td>
                                )}
                                {columnVisibility.latency && <Td>N/A</Td>}
                                {columnVisibility.recording && (
                                  <Td>
                                    {call.recording_url ? (
                                      <IconButton
                                        aria-label="Play recording"
                                        icon={
                                          currentlyPlaying === call.call_id ? (
                                            <FaStopCircle />
                                          ) : (
                                            <FaPlayCircle />
                                          )
                                        }
                                        size="xs"
                                        variant="ghost"
                                        colorScheme="blue"
                                        onClick={(e) =>
                                          playAudio(
                                            call.call_id,
                                            call.recording_url,
                                            e,
                                          )
                                        }
                                      />
                                    ) : (
                                      <Text fontSize="xs" color="gray.500">
                                        N/A
                                      </Text>
                                    )}
                                  </Td>
                                )}
                                {columnVisibility.logs && (
                                  <Td>
                                    {call.public_log_url ? (
                                      <IconButton
                                        aria-label="Download logs"
                                        icon={<FaDownload />}
                                        size="xs"
                                        variant="ghost"
                                        colorScheme="blue"
                                        onClick={(e) =>
                                          downloadLogs(
                                            call.call_id,
                                            call.public_log_url,
                                            e,
                                          )
                                        }
                                      />
                                    ) : (
                                      <Text fontSize="xs" color="gray.500">
                                        N/A
                                      </Text>
                                    )}
                                  </Td>
                                )}
                              </Tr>
                            ))}
                          </Tbody>
                        </Table>
                      </Box>

                      {totalPages > 1 && (
                        <Flex
                          justify="space-between"
                          align="center"
                          mt={6}
                          px={2}
                        >
                          <Text fontSize="sm" color="gray.600">
                            Page {currentPage} of {totalPages} • Total Calls:{" "}
                            {callDetails.length.toLocaleString()}
                          </Text>

                          <HStack spacing={2}>
                            <IconButton
                              aria-label="Previous page"
                              icon={<FiChevronLeft size={18} />}
                              size="sm"
                              variant="ghost"
                              onClick={() =>
                                setCurrentPage((prev) => Math.max(1, prev - 1))
                              }
                              isDisabled={currentPage === 1}
                              _hover={{ bg: "gray.100" }}
                            />

                            {getPageNumbers().map((page, index) =>
                              page === "..." ? (
                                <Text
                                  key={`ellipsis-${index}`}
                                  px={2}
                                  color="gray.500"
                                >
                                  ...
                                </Text>
                              ) : (
                                <Button
                                  key={page}
                                  size="sm"
                                  variant={
                                    currentPage === page ? "solid" : "ghost"
                                  }
                                  colorScheme={
                                    currentPage === page ? "blue" : "gray"
                                  }
                                  onClick={() => setCurrentPage(page as number)}
                                  minW="40px"
                                  _hover={{
                                    bg:
                                      currentPage === page
                                        ? undefined
                                        : "gray.100",
                                  }}
                                >
                                  {page}
                                </Button>
                              ),
                            )}

                            <IconButton
                              aria-label="Next page"
                              icon={<FiChevronRight size={18} />}
                              size="sm"
                              variant="ghost"
                              onClick={() =>
                                setCurrentPage((prev) =>
                                  Math.min(totalPages, prev + 1),
                                )
                              }
                              isDisabled={currentPage === totalPages}
                              _hover={{ bg: "gray.100" }}
                            />
                          </HStack>
                        </Flex>
                      )}
                    </>
                  )}
                </CardBody>
              </Card>
            )}

            {activeMetric === "sms" && (
              <Card mb={8}>
                <CardHeader
                  bg="gradient-to-r from-purple-50 to-pink-50"
                  borderBottom="1px solid"
                  borderColor="gray.200"
                >
                  <Flex justify="space-between" align="center" wrap="wrap" gap={2}>
                    <Heading size="md">SMS Conversations</Heading>
                    <Input
                      size="sm"
                      maxW="200px"
                      placeholder="Search by phone…"
                      value={filterSmsPhone}
                      onChange={(e) => setFilterSmsPhone(e.target.value)}
                    />
                  </Flex>
                </CardHeader>
                <CardBody>
                  {isLoadingSMS ? (
                    <Center py={8}>
                      <Spinner size="lg" color="purple.500" />
                    </Center>
                  ) : smsConversations.length === 0 ? (
                    <Text color="gray.600" textAlign="center" py={4}>
                      No SMS conversations available
                    </Text>
                  ) : (
                    <>
                      <Box overflowX="auto">
                        <Table variant="simple" size="sm">
                          <Thead>
                            <Tr>
                              <Th>Timestamp</Th>
                              <Th>Lead Phone</Th>
                              <Th>Agent Phone</Th>
                              <Th>Agent Name</Th>
                              <Th>Messages</Th>
                            </Tr>
                          </Thead>
                          <Tbody>
                            {paginatedSMS.map((conversation: any) => (
                              <Tr
                                key={`${conversation.sender_id}-${conversation.receiver_id}`}
                                onClick={() => {
                                  setSelectedSMSConversation(conversation);
                                  setIsSMSDialogOpen(true);
                                }}
                                _hover={{
                                  bg: "gray.50",
                                  transform: "translateX(2px)",
                                  transition: "all 0.2s ease",
                                }}
                                transition="all 0.2s ease"
                                cursor="pointer"
                              >
                                <Td>
                                  {new Date(
                                    conversation.first_message_time,
                                  ).toLocaleString()}{" "}
                                  -{" "}
                                  {new Date(
                                    conversation.last_message_time,
                                  ).toLocaleString()}
                                </Td>
                                <Td>{conversation.sender_id}</Td>
                                <Td>{conversation.receiver_id}</Td>
                                <Td>{conversation.agent_name}</Td>
                                <Td>
                                  <Badge colorScheme="purple">
                                    {conversation.message_count}
                                  </Badge>
                                </Td>
                              </Tr>
                            ))}
                          </Tbody>
                        </Table>
                      </Box>

                      {smsTotalPages > 1 && (
                        <Flex
                          justify="space-between"
                          align="center"
                          mt={6}
                          px={2}
                        >
                          <Text fontSize="sm" color="gray.600">
                            Page {smsCurrentPage} of {smsTotalPages} • Total
                            Conversations: {smsConversations.length}
                          </Text>
                          <HStack spacing={2}>
                            <IconButton
                              aria-label="Previous page"
                              icon={<FiChevronLeft />}
                              size="sm"
                              isDisabled={smsCurrentPage === 1}
                              onClick={() =>
                                setSmsCurrentPage((p) => Math.max(1, p - 1))
                              }
                            />
                            {getPageNumbersForTable(
                              smsTotalPages,
                              smsCurrentPage,
                            ).map((page, idx) =>
                              typeof page === "number" ? (
                                <Button
                                  key={idx}
                                  size="sm"
                                  variant={
                                    page === smsCurrentPage ? "solid" : "ghost"
                                  }
                                  colorScheme={
                                    page === smsCurrentPage ? "purple" : "gray"
                                  }
                                  onClick={() => setSmsCurrentPage(page)}
                                >
                                  {page}
                                </Button>
                              ) : (
                                <Text key={idx} px={2} color="gray.500">
                                  {page}
                                </Text>
                              ),
                            )}
                            <IconButton
                              aria-label="Next page"
                              icon={<FiChevronRight />}
                              size="sm"
                              isDisabled={smsCurrentPage === smsTotalPages}
                              onClick={() =>
                                setSmsCurrentPage((p) =>
                                  Math.min(smsTotalPages, p + 1),
                                )
                              }
                            />
                          </HStack>
                        </Flex>
                      )}
                    </>
                  )}
                </CardBody>
              </Card>
            )}

            {activeMetric === "email" && (
              <Card mb={8}>
                <CardHeader
                  bg="gradient-to-r from-orange-50 to-yellow-50"
                  borderBottom="1px solid"
                  borderColor="gray.200"
                >
                  <Flex justify="space-between" align="center" wrap="wrap" gap={2}>
                    <Heading size="md">Email Conversations</Heading>
                    <Input
                      size="sm"
                      maxW="200px"
                      placeholder="Search by phone…"
                      value={filterEmailPhone}
                      onChange={(e) => setFilterEmailPhone(e.target.value)}
                    />
                  </Flex>
                </CardHeader>
                <CardBody>
                  {isLoadingEmail ? (
                    <Center py={8}>
                      <Spinner size="lg" color="orange.500" />
                    </Center>
                  ) : emailConversations.length === 0 ? (
                    <Text color="gray.600" textAlign="center" py={4}>
                      No email conversations available
                    </Text>
                  ) : (
                    <>
                      <Box overflowX="auto">
                        <Table variant="simple" size="sm">
                          <Thead>
                            <Tr>
                              <Th>Timestamp</Th>
                              <Th>Lead Email</Th>
                              <Th>Agent Email</Th>
                              <Th>Agent Name</Th>
                              <Th>Emails</Th>
                            </Tr>
                          </Thead>
                          <Tbody>
                            {paginatedEmail.map((conversation: any) => (
                              <Tr
                                key={`${conversation.sender_id}-${conversation.receiver_id}`}
                                onClick={() => {
                                  setSelectedEmailConversation(conversation);
                                  setIsEmailDialogOpen(true);
                                }}
                                _hover={{
                                  bg: "gray.50",
                                  transform: "translateX(2px)",
                                  transition: "all 0.2s ease",
                                }}
                                transition="all 0.2s ease"
                                cursor="pointer"
                              >
                                <Td>
                                  {new Date(
                                    conversation.first_message_time,
                                  ).toLocaleString()}{" "}
                                  -{" "}
                                  {new Date(
                                    conversation.last_message_time,
                                  ).toLocaleString()}
                                </Td>
                                <Td>{conversation.sender_id}</Td>
                                <Td>{conversation.receiver_id}</Td>
                                <Td>{conversation.agent_name}</Td>
                                <Td>
                                  <Badge colorScheme="orange">
                                    {conversation.message_count}
                                  </Badge>
                                </Td>
                              </Tr>
                            ))}
                          </Tbody>
                        </Table>
                      </Box>

                      {emailTotalPages > 1 && (
                        <Flex
                          justify="space-between"
                          align="center"
                          mt={6}
                          px={2}
                        >
                          <Text fontSize="sm" color="gray.600">
                            Page {emailCurrentPage} of {emailTotalPages} • Total
                            Conversations: {emailConversations.length}
                          </Text>
                          <HStack spacing={2}>
                            <IconButton
                              aria-label="Previous page"
                              icon={<FiChevronLeft />}
                              size="sm"
                              isDisabled={emailCurrentPage === 1}
                              onClick={() =>
                                setEmailCurrentPage((p) => Math.max(1, p - 1))
                              }
                            />
                            {getPageNumbersForTable(
                              emailTotalPages,
                              emailCurrentPage,
                            ).map((page, idx) =>
                              typeof page === "number" ? (
                                <Button
                                  key={idx}
                                  size="sm"
                                  variant={
                                    page === emailCurrentPage
                                      ? "solid"
                                      : "ghost"
                                  }
                                  colorScheme={
                                    page === emailCurrentPage
                                      ? "orange"
                                      : "gray"
                                  }
                                  onClick={() => setEmailCurrentPage(page)}
                                >
                                  {page}
                                </Button>
                              ) : (
                                <Text key={idx} px={2} color="gray.500">
                                  {page}
                                </Text>
                              ),
                            )}
                            <IconButton
                              aria-label="Next page"
                              icon={<FiChevronRight />}
                              size="sm"
                              isDisabled={emailCurrentPage === emailTotalPages}
                              onClick={() =>
                                setEmailCurrentPage((p) =>
                                  Math.min(emailTotalPages, p + 1),
                                )
                              }
                            />
                          </HStack>
                        </Flex>
                      )}
                    </>
                  )}
                </CardBody>
              </Card>
            )}

            {activeMetric === "whatsapp" && (
              <Card mb={8}>
                <CardHeader
                  bg="gradient-to-r from-green-50 to-teal-50"
                  borderBottom="1px solid"
                  borderColor="gray.200"
                >
                  <Flex justify="space-between" align="center" wrap="wrap" gap={2}>
                    <Heading size="md">WhatsApp Conversations</Heading>
                    <Input
                      size="sm"
                      maxW="200px"
                      placeholder="Search by phone…"
                      value={filterWhatsappPhone}
                      onChange={(e) => setFilterWhatsappPhone(e.target.value)}
                    />
                  </Flex>
                </CardHeader>
                <CardBody>
                  {isLoadingWhatsApp ? (
                    <Center py={8}>
                      <Spinner size="lg" color="green.500" />
                    </Center>
                  ) : whatsappConversations.length === 0 ? (
                    <Text color="gray.600" textAlign="center" py={4}>
                      No WhatsApp conversations available
                    </Text>
                  ) : (
                    <>
                      <Box overflowX="auto">
                        <Table variant="simple" size="sm">
                          <Thead>
                            <Tr>
                              <Th>Timestamp</Th>
                              <Th>Lead Phone</Th>
                              <Th>Agent Phone</Th>
                              <Th>Agent Name</Th>
                              <Th>Messages</Th>
                            </Tr>
                          </Thead>
                          <Tbody>
                            {paginatedWhatsApp.map((conversation: any) => (
                              <Tr
                                key={`${conversation.sender_id}-${conversation.receiver_id}`}
                                onClick={() => {
                                  setSelectedWhatsAppConversation(conversation);
                                  setIsWhatsAppDialogOpen(true);
                                }}
                                _hover={{
                                  bg: "gray.50",
                                  transform: "translateX(2px)",
                                  transition: "all 0.2s ease",
                                }}
                                transition="all 0.2s ease"
                                cursor="pointer"
                              >
                                <Td>
                                  {new Date(
                                    conversation.first_message_time,
                                  ).toLocaleString()}{" "}
                                  -{" "}
                                  {new Date(
                                    conversation.last_message_time,
                                  ).toLocaleString()}
                                </Td>
                                <Td>{conversation.sender_id}</Td>
                                <Td>{conversation.receiver_id}</Td>
                                <Td>{conversation.agent_name}</Td>
                                <Td>
                                  <Badge colorScheme="green">
                                    {conversation.message_count}
                                  </Badge>
                                </Td>
                              </Tr>
                            ))}
                          </Tbody>
                        </Table>
                      </Box>

                      {whatsappTotalPages > 1 && (
                        <Flex
                          justify="space-between"
                          align="center"
                          mt={6}
                          px={2}
                        >
                          <Text fontSize="sm" color="gray.600">
                            Page {whatsappCurrentPage} of {whatsappTotalPages} •
                            Total Conversations: {whatsappConversations.length}
                          </Text>
                          <HStack spacing={2}>
                            <IconButton
                              aria-label="Previous page"
                              icon={<FiChevronLeft />}
                              size="sm"
                              isDisabled={whatsappCurrentPage === 1}
                              onClick={() =>
                                setWhatsappCurrentPage((p) =>
                                  Math.max(1, p - 1),
                                )
                              }
                            />
                            {getPageNumbersForTable(
                              whatsappTotalPages,
                              whatsappCurrentPage,
                            ).map((page, idx) =>
                              typeof page === "number" ? (
                                <Button
                                  key={idx}
                                  size="sm"
                                  variant={
                                    page === whatsappCurrentPage
                                      ? "solid"
                                      : "ghost"
                                  }
                                  colorScheme={
                                    page === whatsappCurrentPage
                                      ? "green"
                                      : "gray"
                                  }
                                  onClick={() => setWhatsappCurrentPage(page)}
                                >
                                  {page}
                                </Button>
                              ) : (
                                <Text key={idx} px={2} color="gray.500">
                                  {page}
                                </Text>
                              ),
                            )}
                            <IconButton
                              aria-label="Next page"
                              icon={<FiChevronRight />}
                              size="sm"
                              isDisabled={
                                whatsappCurrentPage === whatsappTotalPages
                              }
                              onClick={() =>
                                setWhatsappCurrentPage((p) =>
                                  Math.min(whatsappTotalPages, p + 1),
                                )
                              }
                            />
                          </HStack>
                        </Flex>
                      )}
                    </>
                  )}
                </CardBody>
              </Card>
            )}

            <Card
              _hover={{
                shadow: "xl",
                transform: "translateY(-4px) scale(1.01)",
                transition: "all 0.3s ease",
              }}
              transition="all 0.3s ease"
            >
              <CardHeader>
                <Heading size="md">Daily Usage</Heading>
              </CardHeader>
              <CardBody>
                {loadingStates.dailyUsage ? (
                  <Center py={8}>
                    <Spinner size="lg" color="blue.500" />
                  </Center>
                ) : dailyUsage.length === 0 ? (
                  <Text color="gray.600" textAlign="center" py={4}>
                    No daily usage data available
                  </Text>
                ) : (
                  <Table variant="simple">
                    <Thead>
                      <Tr>
                        <Th>Date</Th>
                        <Th isNumeric>Calls</Th>
                        <Th isNumeric>Minutes</Th>
                        <Th isNumeric>SMS</Th>
                        {isMainApp && <Th isNumeric>Emails</Th>}
                        <Th isNumeric>WhatsApp</Th>
                        <Th isNumeric>Cost ($)</Th>
                      </Tr>
                    </Thead>
                    <Tbody>
                      {dailyUsage.map((day, index) => (
                        <Tr
                          key={index}
                          _hover={{
                            bg: "blue.50",
                            transform: "translateX(4px)",
                            transition: "all 0.2s ease",
                          }}
                          transition="all 0.2s ease"
                          cursor="pointer"
                        >
                          <Td>
                            {day.dateRange
                              ? day.date
                              : new Date(day.date + "T00:00:00").toLocaleDateString()}
                          </Td>
                          <Td isNumeric>{day.calls}</Td>
                          <Td isNumeric>{(day.minutes || 0).toFixed(2)}</Td>
                          <Td isNumeric>{day.sms}</Td>
                          {isMainApp && <Td isNumeric>{day.emails}</Td>}
                          <Td isNumeric>{day.whatsapp}</Td>
                          <Td isNumeric fontWeight="bold" color="green.600">
                            ${day.cost.toFixed(2)}
                          </Td>
                        </Tr>
                      ))}
                    </Tbody>
                  </Table>
                )}
              </CardBody>
            </Card>

            <Card
              _hover={{
                shadow: "xl",
                transform: "translateY(-4px) scale(1.01)",
                transition: "all 0.3s ease",
              }}
              transition="all 0.3s ease"
            >
              <CardHeader>
                <Heading size="md">Usage by Agent</Heading>
              </CardHeader>
              <CardBody>
                {loadingStates.agentUsage ? (
                  <Center py={8}>
                    <Spinner size="lg" color="blue.500" />
                  </Center>
                ) : agentUsage.length === 0 ? (
                  <Text color="gray.600" textAlign="center" py={4}>
                    No agent usage data available
                  </Text>
                ) : (
                  <Table variant="simple">
                    <Thead>
                      <Tr>
                        <Th>Agent Name</Th>
                        <Th isNumeric>Calls</Th>
                        <Th isNumeric>Minutes</Th>
                        <Th isNumeric>Cost ($)</Th>
                      </Tr>
                    </Thead>
                    <Tbody>
                      {agentUsage.map((agent, index) => (
                        <Tr
                          key={index}
                          _hover={{
                            bg: "purple.50",
                            transform: "translateX(4px)",
                            transition: "all 0.2s ease",
                          }}
                          transition="all 0.2s ease"
                          cursor="pointer"
                        >
                          <Td fontWeight="medium">{agent.name}</Td>
                          <Td isNumeric>{agent.calls}</Td>
                          <Td isNumeric>{(agent.minutes || 0).toFixed(2)}</Td>
                          <Td isNumeric fontWeight="bold" color="green.600">
                            ${agent.cost.toFixed(2)}
                          </Td>
                        </Tr>
                      ))}
                    </Tbody>
                  </Table>
                )}
              </CardBody>
            </Card>

            <Card
              _hover={{
                shadow: "xl",
                transform: "translateY(-4px) scale(1.01)",
                transition: "all 0.3s ease",
              }}
              transition="all 0.3s ease"
            >
              <CardHeader>
                <Heading size="md">Peak Hours Analysis</Heading>
              </CardHeader>
              <CardBody>
                <Grid
                  templateColumns="repeat(auto-fit, minmax(200px, 1fr))"
                  gap={6}
                >
                  <Box
                    textAlign="center"
                    p={4}
                    bg="blue.50"
                    borderRadius="md"
                    _hover={{
                      bg: "blue.100",
                      transform: "scale(1.05)",
                      transition: "all 0.2s ease",
                    }}
                    transition="all 0.2s ease"
                    cursor="pointer"
                  >
                    <Text
                      fontSize="2xl"
                      fontWeight="bold"
                      color="blue.600"
                      mb={2}
                    >
                      {renderValue(
                        hasPeak ? usageStats.peakHours : null,
                        loadingStates.callStats,
                        hasPeak,
                      )}
                    </Text>
                    <Text fontSize="sm" color="blue.800" fontWeight="medium">
                      Peak Hours
                    </Text>
                    <Text fontSize="xs" color="blue.600">
                      Highest activity period
                    </Text>
                  </Box>

                  <Box
                    textAlign="center"
                    p={4}
                    bg="purple.50"
                    borderRadius="md"
                    _hover={{
                      bg: "purple.100",
                      transform: "scale(1.05)",
                      transition: "all 0.2s ease",
                    }}
                    transition="all 0.2s ease"
                    cursor="pointer"
                  >
                    <Text
                      fontSize="2xl"
                      fontWeight="bold"
                      color="purple.600"
                      mb={2}
                    >
                      {renderValue(
                        hasTopAgent ? usageStats.topAgent : null,
                        loadingStates.agentUsage,
                        hasTopAgent,
                      )}
                    </Text>
                    <Text fontSize="sm" color="purple.800" fontWeight="medium">
                      Top Agent
                    </Text>
                    <Text fontSize="xs" color="purple.600">
                      Most active agent
                    </Text>
                  </Box>

                  <Box
                    textAlign="center"
                    p={4}
                    bg="green.50"
                    borderRadius="md"
                    _hover={{
                      bg: "green.100",
                      transform: "scale(1.05)",
                      transition: "all 0.2s ease",
                    }}
                    transition="all 0.2s ease"
                    cursor="pointer"
                  >
                    <Text
                      fontSize="2xl"
                      fontWeight="bold"
                      color="green.600"
                      mb={2}
                    >
                      {renderValue(
                        hasAvgDur ? String(usageStats.avgCallDuration) : null,
                        loadingStates.callStats,
                        hasAvgDur,
                      )}
                    </Text>
                    <Text fontSize="sm" color="green.800" fontWeight="medium">
                      Avg Call Duration
                    </Text>
                    <Text fontSize="xs" color="green.600">
                      Per call average
                    </Text>
                  </Box>
                </Grid>
              </CardBody>
            </Card>

            <Card
              _hover={{
                shadow: "xl",
                transform: "translateY(-4px) scale(1.01)",
                transition: "all 0.3s ease",
              }}
              transition="all 0.3s ease"
            >
              <CardHeader>
                <Heading size="md">Usage Insights</Heading>
              </CardHeader>
              <CardBody>
                {isAnyLoading &&
                !dailyUsage.length &&
                !hasCalls &&
                !hasMinutes ? (
                  <Center py={8}>
                    <Spinner size="lg" color="blue.500" />
                  </Center>
                ) : (
                  <>
                    <Grid
                      templateColumns="repeat(auto-fit, minmax(250px, 1fr))"
                      gap={6}
                    >
                      <Box
                        textAlign="center"
                        p={4}
                        bg="blue.50"
                        borderRadius="md"
                        _hover={{
                          bg: "blue.100",
                          transform: "scale(1.05)",
                          transition: "all 0.2s ease",
                        }}
                        transition="all 0.2s ease"
                        cursor="pointer"
                      >
                        <Text
                          fontSize="2xl"
                          fontWeight="bold"
                          color="blue.600"
                          mb={2}
                        >
                          {renderValue(
                            hasCalls ? String(usageStats.totalCalls) : null,
                            loadingStates.callStats,
                            hasCalls,
                          )}
                        </Text>
                        <Text
                          fontSize="sm"
                          color="blue.800"
                          fontWeight="medium"
                        >
                          Total Calls
                        </Text>
                        <Text fontSize="xs" color="blue.600">
                          In selected period
                        </Text>
                      </Box>

                      <Box
                        textAlign="center"
                        p={4}
                        bg="green.50"
                        borderRadius="md"
                        _hover={{
                          bg: "green.100",
                          transform: "scale(1.05)",
                          transition: "all 0.2s ease",
                        }}
                        transition="all 0.2s ease"
                        cursor="pointer"
                      >
                        <Text
                          fontSize="2xl"
                          fontWeight="bold"
                          color="green.600"
                          mb={2}
                        >
                          {loadingStates.dailyUsage &&
                          dailyUsage.length === 0 ? (
                            <InlineSpinner />
                          ) : dailyUsage.length > 0 ? (
                            `$${dailyUsage.reduce((sum, day) => sum + day.cost, 0).toFixed(2)}`
                          ) : (
                            <Text color="gray.400">—</Text>
                          )}
                        </Text>
                        <Text
                          fontSize="sm"
                          color="green.800"
                          fontWeight="medium"
                        >
                          Total Cost
                        </Text>
                        <Text fontSize="xs" color="green.600">
                          {loadingStates.dailyUsage && dailyUsage.length === 0
                            ? ""
                            : dailyUsage.length > 0
                              ? `Average $${(
                                  dailyUsage.reduce(
                                    (sum, day) => sum + day.cost,
                                    0,
                                  ) / dailyUsage.length
                                ).toFixed(2)}/day`
                              : "No data"}
                        </Text>
                      </Box>

                      <Box
                        textAlign="center"
                        p={4}
                        bg="purple.50"
                        borderRadius="md"
                        _hover={{
                          bg: "purple.100",
                          transform: "scale(1.05)",
                          transition: "all 0.2s ease",
                        }}
                        transition="all 0.2s ease"
                        cursor="pointer"
                      >
                        <Text
                          fontSize="2xl"
                          fontWeight="bold"
                          color="purple.600"
                          mb={2}
                        >
                          {renderValue(
                            hasMinutes ? String(usageStats.totalMinutes) : null,
                            loadingStates.callStats,
                            hasMinutes,
                          )}
                        </Text>
                        <Text
                          fontSize="sm"
                          color="purple.800"
                          fontWeight="medium"
                        >
                          Total Minutes
                        </Text>
                        <Text fontSize="xs" color="purple.600">
                          Call duration
                        </Text>
                      </Box>
                    </Grid>

                    <Box mt={4} p={4} bg="blue.50" borderRadius="md">
                      <Text
                        fontSize="sm"
                        fontWeight="bold"
                        color="blue.800"
                        mb={1}
                      >
                        Performance Summary
                      </Text>
                      <Text fontSize="sm" color="blue.600">
                        {renderValue(
                          hasPeak && hasTopAgent
                            ? `Highest activity during ${usageStats.peakHours} with ${usageStats.topAgent} leading performance`
                            : null,
                          loadingStates.callStats || loadingStates.agentUsage,
                          hasPeak && hasTopAgent,
                        )}
                      </Text>
                    </Box>
                  </>
                )}
              </CardBody>
            </Card>
          </>
        )}
      </VStack>

      <CustomizeFieldsDialog
        isOpen={isCustomizeFieldsDialogOpen}
        onClose={() => setIsCustomizeFieldsDialogOpen(false)}
        onApply={handleColumnVisibilityChange}
        currentVisibility={columnVisibility}
      />

      <DateRangeDialog
        isOpen={isDateRangeDialogOpen}
        onClose={() => setIsDateRangeDialogOpen(false)}
        onApply={handleDateRangeApply}
        currentStartDate={callDetailsDateRange?.start}
        currentEndDate={callDetailsDateRange?.end}
      />

      <CallDetailsDialog
        isOpen={isCallDetailsDialogOpen}
        onClose={() => {
          setIsCallDetailsDialogOpen(false);
          setSelectedCall(null);
        }}
        callData={selectedCall}
      />

      <SMSDetailsDialog
        isOpen={isSMSDialogOpen}
        onClose={() => setIsSMSDialogOpen(false)}
        conversation={selectedSMSConversation}
      />

      <EmailDetailsDialog
        isOpen={isEmailDialogOpen}
        onClose={() => setIsEmailDialogOpen(false)}
        conversation={selectedEmailConversation}
      />

      <WhatsAppDetailsDialog
        isOpen={isWhatsAppDialogOpen}
        onClose={() => setIsWhatsAppDialogOpen(false)}
        conversation={selectedWhatsAppConversation}
      />
    </Container>
  );
};

export default UsagePage;
